import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BusinessImpact, ProviderDataClassification, ProviderLifecycleStatus, RiskLevel } from '@kfolks/types'
import { UserRole } from '@kfolks/types'
import { roleAtLeast } from '@/auth'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import { Input } from '@/shared/components/atoms/ui/input'
import { useFilteredQuery } from '@/shared/hooks/useFilteredQuery'
import { useCreateProvider, useUpdateProvider, useDeleteProvider } from '../hooks/useProviders'
import type { ProviderFormData } from '../hooks/useProviders'
import { useAuth } from '@/hooks/useAuth'
import { EmployeeAutocomplete } from '@/components/EmployeeAutocomplete'
import { FilterBar } from '@/shared/components/molecules/FilterBar'

interface ProviderResponse {
	providerId: string
	name: string
	code: string
	description?: string
	logoUrl?: string
	isActive: boolean
	ownerId?: string | null
	lifecycleStatus: string
	dataClassification?: string[]
	businessImpact: string
	riskLevel: string
}

const LIFECYCLE_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
	[ProviderLifecycleStatus.ACTIVE]: 'default',
	[ProviderLifecycleStatus.PLANNED]: 'outline',
	[ProviderLifecycleStatus.POC]: 'secondary',
	[ProviderLifecycleStatus.TERMINATED]: 'destructive',
}

const LIFECYCLE_LABELS: Record<string, string> = {
	[ProviderLifecycleStatus.ACTIVE]: 'Active',
	[ProviderLifecycleStatus.PLANNED]: 'Planned',
	[ProviderLifecycleStatus.POC]: 'PoC',
	[ProviderLifecycleStatus.TERMINATED]: 'Terminated',
}

const RISK_COLORS: Record<string, string> = {
	[RiskLevel.HIGH]: 'bg-red-100 text-red-800',
	[RiskLevel.MEDIUM]: 'bg-yellow-100 text-yellow-800',
	[RiskLevel.LOW]: 'bg-green-100 text-green-800',
}

const DATA_CLASSIFICATION_LABELS: Record<string, string> = {
	[ProviderDataClassification.PII]: 'PII',
	[ProviderDataClassification.PHI]: 'PHI',
	[ProviderDataClassification.CUSTOMER_DATA]: 'Customer Data',
	[ProviderDataClassification.BUSINESS_DATA]: 'Business Data',
	[ProviderDataClassification.FINANCIAL_DATA]: 'Financial Data',
	[ProviderDataClassification.EMPLOYEE_PERSONAL_DATA]: 'Employee Data',
	[ProviderDataClassification.MARKETING_DATA]: 'Marketing Data',
	[ProviderDataClassification.PAYMENT]: 'Payment',
	[ProviderDataClassification.OTHER]: 'Other',
}

const PROVIDER_FILTERS = [
	{ key: 'name', label: 'Name', type: 'text' as const },
	{
		key: 'lifecycleStatus',
		label: 'Lifecycle',
		type: 'select' as const,
		options: [
			{ label: 'Active', value: ProviderLifecycleStatus.ACTIVE },
			{ label: 'Planned', value: ProviderLifecycleStatus.PLANNED },
			{ label: 'PoC', value: ProviderLifecycleStatus.POC },
			{ label: 'Terminated', value: ProviderLifecycleStatus.TERMINATED },
		],
	},
	{
		key: 'riskLevel',
		label: 'Risk Level',
		type: 'select' as const,
		options: [
			{ label: 'High', value: RiskLevel.HIGH },
			{ label: 'Medium', value: RiskLevel.MEDIUM },
			{ label: 'Low', value: RiskLevel.LOW },
		],
	},
]

const EMPTY_FORM: ProviderFormData = {
	name: '',
	code: '',
	description: '',
	lifecycleStatus: ProviderLifecycleStatus.ACTIVE,
	businessImpact: BusinessImpact.NON_CRITICAL,
	riskLevel: RiskLevel.LOW,
	dataClassification: [],
}

export function ProvidersPage() {
	const { user } = useAuth()
	const isSuperAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)
	const {
		data: providers,
		isLoading,
		tempFilters,
		setTempFilters,
		applyFilters,
		clearFilters,
	} = useFilteredQuery<ProviderResponse>({
		endpoint: '/providers',
		queryKey: 'providers',
	})
	const createMutation = useCreateProvider()
	const updateMutation = useUpdateProvider()
	const deleteMutation = useDeleteProvider()

	const [showCreateModal, setShowCreateModal] = useState(false)
	const [editingProvider, setEditingProvider] = useState<ProviderResponse | null>(null)
	const [formData, setFormData] = useState<ProviderFormData>(EMPTY_FORM)

	const resetForm = () => {
		setFormData(EMPTY_FORM)
		setShowCreateModal(false)
		setEditingProvider(null)
	}

	const handleCreate = async () => {
		if (!formData.name || !formData.code) return
		try {
			await createMutation.mutateAsync(formData)
			resetForm()
		} catch {
			// Error is displayed via createMutation.isError
		}
	}

	const handleUpdate = async () => {
		if (!editingProvider) return
		try {
			await updateMutation.mutateAsync({ id: editingProvider.providerId, data: formData })
			resetForm()
		} catch {
			// Error is displayed via updateMutation.isError
		}
	}

	const handleDelete = async (id: string) => {
		if (!confirm('Are you sure you want to delete this provider?')) return
		await deleteMutation.mutateAsync(id)
	}

	const openEdit = (provider: ProviderResponse) => {
		setEditingProvider(provider)
		setFormData({
			name: provider.name,
			code: provider.code,
			description: provider.description ?? '',
			ownerId: provider.ownerId ?? undefined,
			ownerDisplayName: '', // Will be resolved by autocomplete on focus
			lifecycleStatus: provider.lifecycleStatus,
			businessImpact: provider.businessImpact,
			riskLevel: provider.riskLevel,
			dataClassification: provider.dataClassification ?? [],
		})
	}

	const toggleClassification = (value: string) => {
		const current = formData.dataClassification ?? []
		setFormData({
			...formData,
			dataClassification: current.includes(value) ? current.filter((c) => c !== value) : [...current, value],
		})
	}

	const renderFormFields = () => (
		<div className="space-y-4">
			<div>
				<label htmlFor="provider-name" className="block text-sm font-medium text-foreground mb-1">
					Name *
				</label>
				<Input
					id="provider-name"
					value={formData.name}
					onChange={(e) => setFormData({ ...formData, name: e.target.value })}
				/>
			</div>
			<div>
				<label htmlFor="provider-code" className="block text-sm font-medium text-foreground mb-1">
					Code *
				</label>
				<Input
					id="provider-code"
					value={formData.code}
					onChange={(e) => setFormData({ ...formData, code: e.target.value })}
					className="font-mono"
					disabled={!!editingProvider}
				/>
			</div>
			<div>
				<label htmlFor="provider-description" className="block text-sm font-medium text-foreground mb-1">
					Description
				</label>
				<textarea
					id="provider-description"
					value={formData.description ?? ''}
					onChange={(e) => setFormData({ ...formData, description: e.target.value })}
					className="w-full border rounded-md px-3 py-2 text-sm"
					rows={3}
				/>
			</div>
			<div>
				<label className="block text-sm font-medium text-foreground mb-1">Owner</label>
				<EmployeeAutocomplete
					value={formData.ownerId}
					displayValue={formData.ownerDisplayName}
					onChange={(employeeId, displayName) =>
						setFormData({ ...formData, ownerId: employeeId ?? undefined, ownerDisplayName: displayName })
					}
					placeholder="Search employee by name..."
				/>
			</div>
			<div className="grid grid-cols-2 gap-4">
				<div>
					<label htmlFor="provider-lifecycle" className="block text-sm font-medium text-foreground mb-1">
						Lifecycle Status
					</label>
					<select
						id="provider-lifecycle"
						value={formData.lifecycleStatus}
						onChange={(e) => setFormData({ ...formData, lifecycleStatus: e.target.value })}
						className="w-full border rounded-md px-3 py-2 text-sm"
					>
						{Object.entries(LIFECYCLE_LABELS).map(([value, label]) => (
							<option key={value} value={value}>
								{label}
							</option>
						))}
					</select>
				</div>
				<div>
					<label htmlFor="provider-risk" className="block text-sm font-medium text-foreground mb-1">
						Risk Level
					</label>
					<select
						id="provider-risk"
						value={formData.riskLevel}
						onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value })}
						className="w-full border rounded-md px-3 py-2 text-sm"
					>
						<option value={RiskLevel.LOW}>Low</option>
						<option value={RiskLevel.MEDIUM}>Medium</option>
						<option value={RiskLevel.HIGH}>High</option>
					</select>
				</div>
			</div>
			<div>
				<label htmlFor="provider-impact" className="block text-sm font-medium text-foreground mb-1">
					Business Impact
				</label>
				<select
					id="provider-impact"
					value={formData.businessImpact}
					onChange={(e) => setFormData({ ...formData, businessImpact: e.target.value })}
					className="w-full border rounded-md px-3 py-2 text-sm"
				>
					<option value={BusinessImpact.NON_CRITICAL}>Non-critical</option>
					<option value={BusinessImpact.CRITICAL}>Critical</option>
				</select>
			</div>
			<div>
				<label className="block text-sm font-medium text-foreground mb-1">Data Classification</label>
				<div className="flex flex-wrap gap-2">
					{Object.entries(DATA_CLASSIFICATION_LABELS).map(([value, label]) => (
						<label key={value} className="flex items-center gap-1 text-sm">
							<input
								type="checkbox"
								checked={(formData.dataClassification ?? []).includes(value)}
								onChange={() => toggleClassification(value)}
								className="rounded"
							/>
							{label}
						</label>
					))}
				</div>
			</div>
		</div>
	)

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
			</div>
		)
	}

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<h1 className="text-2xl font-bold text-foreground">Providers</h1>
				{isSuperAdmin && (
					<Button onClick={() => setShowCreateModal(true)} size="sm">
						Create Provider
					</Button>
				)}
			</div>

			<FilterBar
				filters={PROVIDER_FILTERS}
				values={tempFilters}
				onChange={setTempFilters}
				onApply={applyFilters}
				onClear={clearFilters}
			/>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{providers.map((provider) => (
					<div key={provider.providerId} className="bg-white shadow rounded-lg p-6">
						<div className="flex items-center justify-between mb-2">
							<Link
								to={`/providers/${provider.providerId}`}
								className="text-lg font-semibold text-foreground hover:text-blue-600"
							>
								{provider.name}
							</Link>
							<div className="flex gap-1">
								<Badge variant={LIFECYCLE_BADGE_VARIANT[provider.lifecycleStatus] ?? 'secondary'}>
									{LIFECYCLE_LABELS[provider.lifecycleStatus] ?? provider.lifecycleStatus}
								</Badge>
							</div>
						</div>
						<p className="text-xs font-mono text-muted-foreground mb-3">{provider.code}</p>
						{provider.description && <p className="text-sm text-muted-foreground mb-3">{provider.description}</p>}
						<div className="flex items-center gap-2 mb-3">
							<span
								className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${RISK_COLORS[provider.riskLevel] ?? 'bg-muted text-foreground'}`}
							>
								Risk: {provider.riskLevel}
							</span>
							<span className="text-xs text-muted-foreground">
								Impact: {provider.businessImpact === 'critical' ? 'Critical' : 'Non-critical'}
							</span>
						</div>
						{provider.dataClassification && provider.dataClassification.length > 0 && (
							<div className="flex flex-wrap gap-1 mb-3">
								{provider.dataClassification.map((dc) => (
									<span
										key={dc}
										className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
									>
										{DATA_CLASSIFICATION_LABELS[dc] ?? dc}
									</span>
								))}
							</div>
						)}
						{isSuperAdmin && (
							<div className="flex gap-2">
								<button
									type="button"
									onClick={() => openEdit(provider)}
									className="text-sm text-indigo-600 hover:text-indigo-900"
								>
									Edit
								</button>
								<button
									type="button"
									onClick={() => void handleDelete(provider.providerId)}
									className="text-sm text-red-600 hover:text-red-900"
								>
									Delete
								</button>
							</div>
						)}
					</div>
				))}
			</div>

			{providers.length === 0 && (
				<div className="bg-white shadow rounded-lg text-center py-12 text-muted-foreground">
					No providers found. Create one to get started.
				</div>
			)}

			{showCreateModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-auto">
						<h2 className="text-lg font-semibold mb-4">Create Provider</h2>
						{renderFormFields()}
						{createMutation.isError && (
							<p className="text-sm text-red-600 mt-2">{(createMutation.error as Error).message}</p>
						)}
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="outline" onClick={resetForm}>
								Cancel
							</Button>
							<Button onClick={() => void handleCreate()} disabled={createMutation.isPending}>
								{createMutation.isPending ? 'Creating...' : 'Create'}
							</Button>
						</div>
					</div>
				</div>
			)}

			{editingProvider && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-auto">
						<h2 className="text-lg font-semibold mb-4">Edit Provider</h2>
						{renderFormFields()}
						{updateMutation.isError && (
							<p className="text-sm text-red-600 mt-2">{(updateMutation.error as Error).message}</p>
						)}
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="outline" onClick={resetForm}>
								Cancel
							</Button>
							<Button onClick={() => void handleUpdate()} disabled={updateMutation.isPending}>
								{updateMutation.isPending ? 'Saving...' : 'Save'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
